import { useMemo, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import Jahresplan from "../Jahresplan";
import LeistungCombo from "../LeistungCombo";
import PositionZeile from "./PositionZeile";
import { Row, MonateFehlen } from "./teile";
import { useKatalogAnlegen, eur } from "../../hooks/useVertraege";
import { einheitLabel } from "../../lib/labels";
import { einsaetze, einsatzMonate } from "../../lib/frequenz";
import {
  kalkVorschau, mengeAusObjekt, positionAusKatalog, vorschlaegeAusGarten, freiePosition, positionTitel,
} from "../../lib/wizard";
import type { KatalogEintrag, VertragPosition, GartenPosition, LeistungsVorschlag } from "../../lib/wizard";

/**
 * Schritt 3 „Leistungen": Vorschläge aus dem Garten, die Positionen im Vertrag,
 * Anfahrt, freie Leistungen — daneben der Jahresplan (volle Breite).
 */
export default function SchrittLeistungen({ positionen, setPositionen, katalog, faktor, garten, gartenErfasst, kalk, ohneMonate, onZurueckZumGarten }: {
  positionen: VertragPosition[];
  setPositionen: Dispatch<SetStateAction<VertragPosition[]>>;
  katalog: KatalogEintrag[];
  faktor: number;
  garten: GartenPosition[];
  gartenErfasst: boolean;
  kalk: ReturnType<typeof kalkVorschau>;
  ohneMonate: number;
  onZurueckZumGarten: () => void;
}) {
  const katalogAnlegen = useKatalogAnlegen();
  const [planOffen, setPlanOffen] = useState(false);

  const vorschlaege = useMemo(() => {
    const drin = new Set(positionen.map(p => p.leistung_id).filter(Boolean));
    return vorschlaegeAusGarten(katalog, garten, faktor).filter(v => !drin.has(v.katalog.id));
  }, [katalog, garten, faktor, positionen]);

  function addLeistung(k: KatalogEintrag, menge?: number) {
    const m = menge ?? mengeAusObjekt(k, { positionen: garten }) ?? 1;
    setPositionen(p => [...p, { ...positionAusKatalog(k, m), sort_order: p.length }]);
  }
  function uebernehmeAlle(liste: LeistungsVorschlag[]) {
    setPositionen(p => [
      ...p,
      ...liste.map((v, i) => ({ ...positionAusKatalog(v.katalog, v.menge), sort_order: p.length + i })),
    ]);
  }
  function addFreieLeistung(titel: string) {
    setPositionen(p => [...p, { ...freiePosition(titel), sort_order: p.length }]);
  }
  function updatePos(i: number, wert: Partial<VertragPosition>) {
    setPositionen(p => p.map((x, idx) => (idx === i ? { ...x, ...wert } : x)));
  }
  function removePos(i: number) { setPositionen(p => p.filter((_, idx) => idx !== i)); }

  /** Eine im Vertrag erfundene Leistung in den Katalog übernehmen — mit allem,
   *  was hier schon steht (Einheit, Preis, Kosten, Zeit, Lohnanteil, Monate als
   *  Standardvorschlag). Danach hängt die Position am Katalogeintrag, damit
   *  Titeländerungen nicht an zwei Stellen gepflegt werden müssen. */
  async function inKatalogAufnehmen(i: number) {
    const pos = positionen[i];
    const titel = positionTitel(pos, katalog).trim();
    if (!titel) return;
    const neu = await katalogAnlegen.mutateAsync({
      titel, kategorie: "sonstiges", einheit: pos.einheit,
      vk_netto: pos.einzelpreis_netto, kosten_netto: pos.kosten_snapshot,
      zeit_min_je_einheit: pos.zeit_min_snapshot, lohnanteil_prozent: pos.lohnanteil_prozent,
      standard_monate: pos.monate,
    });
    updatePos(i, { leistung_id: neu.id, titel_override: null });
  }

  const monate = einsatzMonate(positionen, katalog);
  const anfahrt = positionen.find(p => p.einheit === "anfahrt");
  const anfahrtKat = katalog.find(k => k.einheit === "anfahrt");

  return (
    <div className="pf-mitplan">
      <div className="pf-wz-spalte">
        <button className="pf-btn is-ghost is-small pf-plan-oeffnen pf-wz-abstand" onClick={() => setPlanOffen(true)}>
          ▤ Jahresplan ansehen
        </button>
        <Vorschlaege
          vorschlaege={vorschlaege}
          gartenErfasst={gartenErfasst}
          onAdd={v => addLeistung(v.katalog, v.menge)}
          onAlle={() => uebernehmeAlle(vorschlaege)}
          onZurueckZumGarten={onZurueckZumGarten}
        />

        <div className="pf-seclabel">Im Vertrag</div>
        {positionen.length === 0 ? (
          <div className="pf-empty">Noch keine Leistung übernommen.</div>
        ) : (
          positionen.map((p, i) => (
            <PositionZeile key={i} p={p} positionen={positionen} katalog={katalog} faktor={faktor}
              onPatch={wert => updatePos(i, wert)} onRemove={() => removePos(i)}
              onKatalogAufnehmen={() => inKatalogAufnehmen(i)}
              aufnahmeLaeuft={katalogAnlegen.isPending} />
          ))
        )}

        {positionen.length > 0 && anfahrtKat && (
          anfahrt ? (
            <div className="pf-wz-klein pf-wz-oben-10">
              ✓ Anfahrt enthalten ({einsaetze(anfahrt)}×/Jahr) — rechnet sich aus den Einsätzen mit.
            </div>
          ) : (
            <div className="pf-wz-anfahrt">
              <button className="pf-btn is-ghost" disabled={monate.length === 0}
                onClick={() => setPositionen(p => [...p, { ...positionAusKatalog(anfahrtKat, 1, monate), sort_order: p.length }])}>
                ＋ Anfahrten ermitteln{monate.length > 0 ? ` (${monate.length}× aus den Einsatzmonaten)` : ""}
              </button>
              <div className="pf-wz-mini pf-wz-oben-6">
                Zum Schluss ergänzen — die Zahl folgt aus den geplanten Einsatzmonaten der Leistungen.
              </div>
            </div>
          )
        )}

        <div className="pf-seclabel">Weitere Leistung hinzufügen</div>
        <LeistungHinzufuegen katalog={katalog} garten={garten} onAdd={addLeistung} onFrei={addFreieLeistung} />

        {positionen.length > 0 && (
          <div className="pf-card pf-wz-zwischenstand">
            <Row l="Zwischenstand Jahreswert" r={eur(kalk.jahres_netto)} strong />
            <Row l="Monatsrate" r={`${eur(kalk.monats_netto)}/Monat`} />
            {ohneMonate > 0 && <MonateFehlen n={ohneMonate} />}
          </div>
        )}
      </div>
      <Jahresplan positionen={positionen} katalog={katalog} faktor={faktor}
        offen={planOffen} onSchliessen={() => setPlanOffen(false)} />
    </div>
  );
}

/**
 * „Passend zu diesem Garten" — der Einstieg in Schritt 3.
 *
 * Der Vorschlag nennt Menge, Satz und den Preis für EINEN Durchgang. Kein
 * Zeitraum: wie oft eine Leistung im Jahr nötig ist, hängt am Garten und am
 * Kunden — ein vorgeschlagener Monatssatz liefe ungeprüft mit und erfände den
 * Jahreswert. Die Monate klickt der Mensch nach dem Übernehmen.
 */
function Vorschlaege({ vorschlaege, gartenErfasst, onAdd, onAlle, onZurueckZumGarten }: {
  vorschlaege: LeistungsVorschlag[];
  gartenErfasst: boolean;
  onAdd: (v: LeistungsVorschlag) => void;
  onAlle: () => void;
  onZurueckZumGarten: () => void;
}) {
  if (!gartenErfasst) {
    return (
      <div className="pf-card pf-wz-unten-14">
        <div className="pf-wz-titel pf-wz-unten-4">Noch kein Garten erfasst</div>
        <div className="pf-wz-text pf-wz-unten">
          Trag im Schritt davor Typ und Größe ein (z. B. 150 m² Rasen, 35 lfm Hecke) —
          dann schlägt die App die passenden Leistungen mit Menge und Preis vor.
        </div>
        <button className="pf-btn is-ghost is-small" onClick={onZurueckZumGarten}>Zurück zum Garten</button>
      </div>
    );
  }
  if (vorschlaege.length === 0) return null;

  return (
    <div className="pf-wz-unten-6">
      <div className="pf-seclabel pf-wz-ohne-oben">Passend zu diesem Garten</div>
      <div className="pf-wz-klein pf-wz-vorschlag-hinweis">
        Preis je Durchgang. Übernommen wird mit den üblichen Monaten aus dem
        Katalog — ändern im Jahresplan.
      </div>
      {vorschlaege.map(v => (
        <div key={v.katalog.id} className="pf-row pf-wz-vorschlag">
          <div className="pf-row-main">
            <div className="pf-wz-titel">{v.katalog.titel}</div>
            <div className="pf-wz-klein pf-wz-oben-2">
              {v.menge.toLocaleString("de-DE")} {einheitLabel(v.katalog.einheit)} × {eur(v.katalog.vk_netto)} · aus {v.herkunft}
            </div>
          </div>
          <div className="pf-row-side">
            <div className="pf-wz-rechts">
              <div className="pf-wz-titel">{eur(v.je_durchgang)}</div>
              <div className="pf-wz-mini">je Durchgang</div>
            </div>
            <button className="pf-btn is-small" onClick={() => onAdd(v)}>Übernehmen</button>
          </div>
        </div>
      ))}
      <button className="pf-btn is-ghost pf-wz-oben-2" onClick={onAlle}>
        Alle {vorschlaege.length} übernehmen
      </button>
    </div>
  );
}

/** Leistung hinzufügen — Katalogsuche im selben Feld, in dem man auch eine
 *  eigene eintippen kann. Ein Vertrag darf nicht daran scheitern, dass eine
 *  Leistung noch nicht angelegt ist; aufgenommen wird sie danach an der
 *  Position selbst. */
function LeistungHinzufuegen({ katalog, garten, onAdd, onFrei }: {
  katalog: KatalogEintrag[]; garten: GartenPosition[];
  onAdd: (k: KatalogEintrag, menge?: number) => void;
  onFrei: (titel: string) => void;
}) {
  const [text, setText] = useState("");
  return (
    <div className="pf-card">
      <LeistungCombo
        wert={text}
        katalog={katalog}
        onText={setText}
        platzhalter="Leistung suchen oder eigene eintragen…"
        onWaehlen={k => {
          onAdd(k, mengeAusObjekt(k, { positionen: garten }) ?? undefined);
          setText("");
        }}
        onEigene={titel => { onFrei(titel); setText(""); }}
      />
      <div className="pf-wz-mini pf-wz-oben-8">
        Passt eine Leistung zum erfassten Garten, wird die Menge gleich mit übernommen.
      </div>
    </div>
  );
}
